import { create } from 'zustand';
// 【追加】Patient型とStaff型をインポート
import { Patient, Staff, NewPatientData, NewStaffData, AppointmentError, Handover, ClinicSpeechTerm } from '../types';
import {
  getAllStaff,
  updateStaff as apiUpdateStaff,
  addStaff as apiAddStaff,
  deleteStaff as apiDeleteStaff,
  addPatient as apiAddPatient,
  getAllPatients,
  updatePatient as apiUpdatePatient,
  deletePatient as apiDeletePatient,
  getAllHandovers,
  createHandoverDB,
  updateHandoverDB,
  deleteHandoverDB,
  insertOperationLog,
  getClinicSpeechTerms,
  clearClinicSpeechTermsSessionCache,
} from '../lib/api';

// 【追加】ユーザーの型を定義
interface User {
  id: string;
  name: string;
  role: 'developer' | 'admin' | 'staff'; // '開発者' or '管理者' or 'スタッフ'
}

interface DashboardState {
  // 現在の医院（テナント）ID（URL `/:clinicId` 由来）
  currentClinicId: string | null;
  setCurrentClinicId: (clinicId: string | null) => void;

  // 【追加】全患者リストをストアで管理
  allPatients: Patient[];
  isAddingPatient: boolean; // この行を追加
  addPatient: (newPatient: NewPatientData) => Promise<Patient | null>; // 戻り値をPromiseに変更
  
  // 【変更】患者フォームパネル関連（名前を変更）
  showPatientFormPanel: boolean;
  setShowPatientFormPanel: (show: boolean) => void;
  editingPatient: Patient | null; // 【追加】編集中の患者データ
  setEditingPatient: (patient: Patient | null) => void; // 【追加】
  /** 編集中患者のみクリア（パネルは開かない。setEditingPatient(null) はパネルを開いてしまうため） */
  clearEditingPatientOnly: () => void;
  
  // 【追加】患者フォームの更新カウンター（アニメーション用）
  patientFormRevision: number;
  isUpdatingPatient: boolean; // ローディング状態
  updatePatient: (patientId: string, updatedData: Partial<Patient>) => Promise<boolean>;
  isDeletingPatient: boolean; // 削除中のローディング状態
  deletePatient: (patientId: string) => Promise<boolean>;

  // 【追加】スタッフ関連の状態管理
  allStaff: Staff[];
  isAddingStaff: boolean;
  addStaff: (newStaff: NewStaffData) => Promise<Staff | null>;
  showStaffFormPanel: boolean;
  setShowStaffFormPanel: (show: boolean) => void;
  editingStaff: Staff | null;
  setEditingStaff: (staff: Staff | null) => void;
  staffFormRevision: number; // この行を追加
  isUpdatingStaff: boolean;
  updateStaff: (staffId: string, updatedData: Partial<Staff>) => Promise<boolean>;
  isDeletingStaff: boolean;
  deleteStaff: (
    staffId: string,
    options?: { replacementStaffId?: string; allowUnassigned?: boolean }
  ) => Promise<{ success: boolean; error?: string }>;

  // 【追加】申し送り関連の状態管理
  allHandovers: Handover[];
  loadHandovers: (patientId?: string) => Promise<void>;
  isAddingHandover: boolean;
  addHandover: (handoverData: { patientId: string; content: string }) => Promise<Handover | null>;
  isUpdatingHandover: boolean;
  updateHandover: (handoverId: string, updatedData: {
    content?: string;
    isCompleted?: boolean;
  }) => Promise<boolean>;
  isDeletingHandover: boolean;
  deleteHandover: (handoverId: string) => Promise<boolean>;

  // 【追加】現在ログイン中のユーザー
  currentUser: User | null;
  setCurrentUser: (user: User | null) => void;
  logout: () => void;

  // Auth context for audit logs
  authUserId: string | null; // Supabase Auth user id (uuid)
  setAuthUserId: (id: string | null) => void;
  isDeviceUser: boolean; // Supabase Auth user kind = device
  setIsDeviceUser: (value: boolean) => void;

  // --- 設定パネル関連 ---
  showSettingsPanel: boolean;
  setShowSettingsPanel: (show: boolean) => void;

  /** 音声用語辞書パネル（全ロール・設定とは独立） */
  showSpeechDictionaryPanel: boolean;
  setShowSpeechDictionaryPanel: (show: boolean) => void;
  speechDictionaryRevision: number;
  openSpeechDictionaryPanel: () => void;
  clinicSpeechTerms: ClinicSpeechTerm[];
  refreshClinicSpeechTerms: () => Promise<void>;

  // --- 設定メニューから開くパネル ---
  showAllPatientListPanel: boolean;
  setShowAllPatientListPanel: (show: boolean) => void;
  openAllPatientList: () => void;
  showStaffListPanel: boolean;
  setShowStaffListPanel: (show: boolean) => void;
  // 診療フロー用スタッフパネル
  showWorkflowStaffListPanel: boolean;
  setShowWorkflowStaffListPanel: (show: boolean) => void;
  workflowStaffListRevision: number;
  // 設定側の日付パネル（最小制御）
  showSettingsDatePanel: boolean;
  setShowSettingsDatePanel: (show: boolean) => void;
  settingsDatePanelRevision: number;

  // --- 日付パネル関連 ---
  showDatePanel: boolean;
  setShowDatePanel: (show: boolean) => void;
  selectedDate: Date | null;
  setSelectedDate: (date: Date | null) => void;
  // ユーザーが日付を手動選択したか（初期の自動選択を除外するため）
  hasUserSelectedDate: boolean;

  // --- 他パネル ---
  selectedPatientId: string | null;
  setSelectedPatient: (id: string | null) => void;

  showDetailPanel: boolean;
  setShowDetailPanel: (show: boolean) => void;

  // スケジュールパネル
  showSchedulePanel: boolean;
  setShowSchedulePanel: (show: boolean) => void;
  schedulePanelRevision: number;
  /** スケジュールパネルを開いた元（診療フロー or 設定フロー）。スマホ版で予約登録の可否判定に使用 */
  schedulePanelSource: 'workflow' | 'settings';
  setSchedulePanelSource: (source: 'workflow' | 'settings') => void;

  // 予約登録用 患者一覧（スケジュール専用）
  showSchedulePatientListPanel: boolean;
  setShowSchedulePatientListPanel: (show: boolean) => void;
  schedulePatientListRevision: number;
  // スケジュール登録用 選択状態
  scheduleSelectedTime: string | null;
  setScheduleSelectedTime: (time: string | null) => void;
  scheduleSelectedStaffId: string | null;
  setScheduleSelectedStaffId: (id: string | null) => void;
  reserveSelectedSlotWithPatient: (patientId: string) => Promise<boolean>;
  // スケジュール表示用: 時刻→患者の簡易インデックス
  scheduleAppointmentsByTime: Record<string, { patientId: string; patientName: string }>;
  loadScheduleAppointments: () => Promise<void>;
  subscribeSchedule: () => void;
  cancelAppointmentAtSelectedSlot: () => Promise<boolean>;
  
  // 【追加】患者一覧のコンテキスト管理
  patientListContext: 'workflow' | 'settings';
  
  // 【追加】設定から患者一覧を開くための関数
  openPatientListFromSettings: () => void;
  
  resetPanels: () => void;
  reset: () => void;
  togglePanels: () => void;
  toggleSettingsWorkflow: () => void;
  
  // 【追加】初期化関数
  initialize: () => Promise<void>;

  // ▼▼▼ 以下カウンターを全て追加 ▼▼▼
  datePanelRevision: number;
  detailPanelRevision: number; // Handover detail
  settingsPanelRevision: number;
  patientListRevision: number; // Settings patient list
  staffListRevision: number;
  
  // 【追加】スマホ版で戻る操作を識別するフラグ
  isNavigatingBack: boolean;
  setIsNavigatingBack: (value: boolean) => void;
  /** スマホ管理者ログイン時: スタッフ一覧をスライドインせず最初から表示するためのフラグ */
  skipWorkflowStaffListAnimation: boolean;
  setSkipWorkflowStaffListAnimation: (value: boolean) => void;
  /** 診療⇔設定フロー切り替え時: スライドアニメーションをスキップして即時表示するためのフラグ */
  skipFlowSwitchAnimation: boolean;
  setSkipFlowSwitchAnimation: (value: boolean) => void;
  /** スマホ: 設定の深い階層から設定トップへ戻す途中（下ナビ設定タップ等）。設定トップを即時表示し手前パネルは右へ退場 */
  settingsReturningToRoot: boolean;
  setSettingsReturningToRoot: (value: boolean) => void;
  /** スマホ: 設定の深い階層から診療へ切り替える途中。診療トップを背面に即時表示し設定オーバーレイは右へ退場 */
  workflowRootRevealFromSettings: boolean;
  setWorkflowRootRevealFromSettings: (value: boolean) => void;
  /** スマホ: 診療フローから設定へ切り替える途中。設定トップを背面に即時表示し診療オーバーレイは右へ退場 */
  settingsRootRevealFromWorkflow: boolean;
  setSettingsRootRevealFromWorkflow: (value: boolean) => void;
  // 戻る動作専用の関数
  navigateBackToPatientList: () => void;
  navigateBackToStaffList: () => void;
  // 診療フロー用の戻る動作専用の関数
  navigateBackToSchedulePanel: () => void;
  navigateBackToDatePanel: () => void;
  navigateBackToWorkflowStaffList: () => void;
  /** PIN変更ダイアログ表示（BottomMenuBar外でレンダリングし、閉じた際の空ダッシュボード問題を回避） */
  showPinChangeDialog: boolean;
  setShowPinChangeDialog: (show: boolean) => void;
  /** PIN変更後のメッセージ（BottomMenuBarで表示） */
  pinChangeMessage: { type: 'success' | 'error'; text: string } | null;
  setPinChangeMessage: (msg: { type: 'success' | 'error'; text: string } | null) => void;
}

export const useDashboardStore = create<DashboardState>((set, get) => ({
  // --- 初期状態 ---
  currentClinicId: null,
  setCurrentClinicId: (clinicId) => set({ currentClinicId: clinicId }),

  isAddingPatient: false, // この行を追加
  // 【変更】データベースから患者データを取得するため、初期状態は空配列
  allPatients: [],
  isUpdatingPatient: false, // ローディング状態の初期値
  isDeletingPatient: false, // 削除中ローディングの初期値
  
  // 【追加】スタッフリストの初期状態（空から開始）
  allStaff: [],
  isAddingStaff: false,

  // 【追加】申し送りリストの初期状態（空から開始）
  allHandovers: [],
  isAddingHandover: false,
  isUpdatingHandover: false,
  isDeletingHandover: false,
  
  // 【変更】患者フォームパネルの初期状態（名前を変更）
  showPatientFormPanel: false,
  editingPatient: null, // 【追加】
  patientFormRevision: 0, // 【追加】更新カウンターの初期値
  
  // 【追加】スタッフフォームパネルの初期状態
  showStaffFormPanel: false,
  editingStaff: null,
  staffFormRevision: 0, // この行を追加
  isUpdatingStaff: false,
  isDeletingStaff: false,
  
  // 【追加】ユーザー情報の初期状態
  currentUser: null,
  authUserId: null,
  isDeviceUser: false,
  
  showSettingsPanel: false,
  showSpeechDictionaryPanel: false,
  speechDictionaryRevision: 0,
  clinicSpeechTerms: [],
  showAllPatientListPanel: false,
  showStaffListPanel: false,
  showWorkflowStaffListPanel: false,
  showSettingsDatePanel: false,
  showDatePanel: false,
  selectedDate: new Date(),
  hasUserSelectedDate: false,
  selectedPatientId: null,
  showDetailPanel: false,
  patientListContext: 'workflow', // デフォルトは診療フロー

  // ▼▼▼ 以下カウンターを全て追加 ▼▼▼
  datePanelRevision: 0,
  detailPanelRevision: 0, // Handover detail
  settingsPanelRevision: 0,
  patientListRevision: 0, // Settings patient list
  staffListRevision: 0,
  workflowStaffListRevision: 0,
  settingsDatePanelRevision: 0,
  schedulePanelRevision: 0,
  schedulePanelSource: 'workflow',
  setSchedulePanelSource: (source) => set({ schedulePanelSource: source }),
  showSchedulePanel: false,
  schedulePatientListRevision: 0,
  showSchedulePatientListPanel: false,
  scheduleSelectedTime: null,
  scheduleSelectedStaffId: null,
  scheduleAppointmentsByTime: {},
  
  // 【追加】スマホ版で戻る操作を識別するフラグ
  isNavigatingBack: false,
  setIsNavigatingBack: (value) => set({ isNavigatingBack: value }),
  skipWorkflowStaffListAnimation: false,
  setSkipWorkflowStaffListAnimation: (value) => set({ skipWorkflowStaffListAnimation: value }),
  skipFlowSwitchAnimation: false,
  setSkipFlowSwitchAnimation: (value) => set({ skipFlowSwitchAnimation: value }),
  settingsReturningToRoot: false,
  setSettingsReturningToRoot: (value) => set({ settingsReturningToRoot: value }),
  workflowRootRevealFromSettings: false,
  setWorkflowRootRevealFromSettings: (value) => set({ workflowRootRevealFromSettings: value }),
  settingsRootRevealFromWorkflow: false,
  setSettingsRootRevealFromWorkflow: (value) => set({ settingsRootRevealFromWorkflow: value }),
  // 戻る動作専用の関数
  navigateBackToPatientList: () => {
    set((state) => ({
      isNavigatingBack: true,
      showPatientFormPanel: false,
      showAllPatientListPanel: true,
      showSpeechDictionaryPanel: false,
      editingPatient: null,
      patientListRevision: state.patientListRevision + 1, // revisionを更新して再マウント
    }));
  },
  navigateBackToStaffList: () => {
    set((state) => ({
      isNavigatingBack: true,
      showStaffFormPanel: false,
      showStaffListPanel: true,
      showPatientFormPanel: false,
      showAllPatientListPanel: false,
      showSettingsDatePanel: false,
      showSchedulePanel: false,
      showSpeechDictionaryPanel: false,
      staffListRevision: state.staffListRevision + 1, // revisionを更新して再マウント
    }));
  },
  // 診療フロー用の戻る動作専用の関数
  navigateBackToSchedulePanel: () => {
    set((state) => {
      const reopenSchedule = !state.showSchedulePanel;
      return {
        isNavigatingBack: true,
        showDetailPanel: false,
        showSchedulePatientListPanel: false, // 予約登録パネルを閉じる
        showSchedulePanel: true,
        scheduleSelectedTime: null, // 空き枠の選択表示を残さない（戻る／予約完了後）
        schedulePanelRevision: reopenSchedule ? state.schedulePanelRevision + 1 : state.schedulePanelRevision,
      };
    });
  },
  navigateBackToDatePanel: () => {
    set((state) => {
      // 月カレが閉じているときだけ再マウント（revision 更新）。診療スマホはスケジュール表示時に月カレを閉じるため戻る操作で多く該当する。
      const reopenCalendar = !state.showDatePanel;
      return {
        isNavigatingBack: true,
        showSchedulePanel: false,
        showDatePanel: true,
        datePanelRevision: reopenCalendar ? state.datePanelRevision + 1 : state.datePanelRevision,
      };
    });
  },
  navigateBackToWorkflowStaffList: () => {
    set((state) => ({
      isNavigatingBack: true,
      showDatePanel: false,
      showWorkflowStaffListPanel: true,
      workflowStaffListRevision: state.workflowStaffListRevision + 1, // revisionを更新して再マウント
    }));
  },
  showPinChangeDialog: false,
  setShowPinChangeDialog: (show) => set({ showPinChangeDialog: show }),
  pinChangeMessage: null,
  setPinChangeMessage: (msg) => set({ pinChangeMessage: msg }),

  // --- 更新ロジック ---
  /**
   * アプリ起動時にデータベースからマスターデータを取得する
   */
  initialize: async () => {
    const { currentClinicId } = get();
    const [staffFromApi, patients, speechTerms] = await Promise.all([
      getAllStaff(currentClinicId ?? undefined),
      getAllPatients(currentClinicId ?? undefined),
      getClinicSpeechTerms(currentClinicId ?? undefined),
    ]);

    set({
      allStaff: staffFromApi,
      allPatients: patients,
      clinicSpeechTerms: speechTerms,
    });
  },
  // 【重要】新しい患者を追加する関数
  addPatient: async (newPatientData) => {
    set({ isAddingPatient: true }); // ローディング開始
    const { currentUser, currentClinicId, authUserId } = get();
    if (!currentUser) {
      console.error("患者追加にはログインが必要です。");
      set({ isAddingPatient: false });
      return null;
    }
    try {
      // API呼び出しと最低1秒の待機を同時に実行
      const [newPatientFromDB] = await Promise.all([
        apiAddPatient(newPatientData, currentUser.id, currentClinicId ?? undefined),
        new Promise(resolve => setTimeout(resolve, 1000))
      ]);

      // 登録が成功し、新しい患者データが返ってきた場合
      if (newPatientFromDB) {
        set((state) => ({
          // データベースから返された情報（正しいIDを含む）でストアを更新
          allPatients: [newPatientFromDB, ...state.allPatients],
        }));
        // Audit log (best-effort)
        if (currentClinicId) {
          void insertOperationLog({
            clinicId: currentClinicId,
            authUserId: authUserId ?? undefined,
            actedStaffId: currentUser?.id ?? null,
            action: 'patient.create',
            targetType: 'patients',
            targetId: String(newPatientFromDB.id),
          });
        }
        return newPatientFromDB; // 成功した場合は患者データを返す
      }
      return null; // 失敗した場合はnullを返す
    } catch (error) {
      console.error("患者の追加中にエラーが発生:", error);
      return null; // エラー時もnullを返す
    } finally {
      set({ isAddingPatient: false }); // 成功・失敗問わずローディング終了
    }
  },
  
  // 【追加】患者情報を更新する関数
  updatePatient: async (patientId, updatedData) => {
    set({ isUpdatingPatient: true });
    const { currentUser, currentClinicId, authUserId } = get();

    const dataWithUpdater = {
      ...updatedData,
      updatedBy: currentUser?.id,
    };

    try {
      // API呼び出しと最低1秒の待機を同時に実行
      const [success] = await Promise.all([
        apiUpdatePatient(patientId, dataWithUpdater),
        new Promise(resolve => setTimeout(resolve, 1000))
      ]);

      if (success) {
        set((state) => {
          const prev = state.allPatients.find((p) => p.id === patientId);
          if (!prev) {
            console.error('updatePatient: patient not in store', patientId);
            return state;
          }
          const updatedPatient = {
            ...prev,
            ...updatedData,
            updatedBy: currentUser?.id ?? prev.updatedBy,
            updatedByName: currentUser?.name ?? prev.updatedByName,
            updatedAt: new Date().toISOString(),
          };

          return {
            allPatients: state.allPatients.map((p) => (p.id === patientId ? updatedPatient : p)),
            editingPatient: state.editingPatient?.id === patientId ? updatedPatient : state.editingPatient,
            patientFormRevision: state.patientFormRevision + 1, // 内側フォームのアニメ用（外側keyには含めない）
          };
        });
        // Audit log (best-effort)
        if (currentClinicId) {
          void insertOperationLog({
            clinicId: currentClinicId,
            authUserId: authUserId ?? undefined,
            actedStaffId: currentUser?.id ?? null,
            action: 'patient.update',
            targetType: 'patients',
            targetId: String(patientId),
          });
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error("患者更新中にエラーが発生:", error);
      return false;
    } finally {
      set({ isUpdatingPatient: false });
    }
  },
  
  deletePatient: async (patientId) => {
    set({ isDeletingPatient: true });
    try {
      const { currentClinicId, authUserId, currentUser } = get();
      const [success] = await Promise.all([
        apiDeletePatient(patientId),
        new Promise(resolve => setTimeout(resolve, 1000))
      ]);

      if (success) {
        set((state) => ({
          // Soft-delete: keep record for history (appointments/handovers), but hide in lists via isActive flag
          // パネル遷移は PatientFormPanel 側（モバイル: 完了モーダル→戻る）
          allPatients: state.allPatients.map((p) => (p.id === patientId ? { ...p, isActive: false } : p)),
        }));
        // Audit log (best-effort)
        if (currentClinicId) {
          void insertOperationLog({
            clinicId: currentClinicId,
            authUserId: authUserId ?? undefined,
            actedStaffId: currentUser?.id ?? null,
            action: 'patient.delete', // semantic delete (archive)
            targetType: 'patients',
            targetId: String(patientId),
          });
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error("患者削除中にエラーが発生:", error);
      return false;
    } finally {
      set({ isDeletingPatient: false });
    }
  },

  // 【変更】患者フォームパネルの更新（名前を変更）
  setShowPatientFormPanel: (show) => set({ showPatientFormPanel: show }),
  setEditingPatient: (patient) => {
    set((state) => ({
      editingPatient: patient,
      showPatientFormPanel: true,
      patientFormRevision: state.patientFormRevision + 1, // 常にカウンターを更新して再描画をトリガー
      showDetailPanel: false,
      // PC版ではパネルを閉じず、スマホ版でのみ閉じる（PatientListPanel.tsxで制御）
    }));
  },
  clearEditingPatientOnly: () => set({ editingPatient: null }),

  // 【追加】スタッフ関連のアクション
  addStaff: async (newStaffData) => {
    set({ isAddingStaff: true });
    const { currentUser, currentClinicId, authUserId } = get();
    if (!currentUser) {
      console.error("スタッフ追加にはログインが必要です。");
      set({ isAddingStaff: false });
      return null;
    }
    try {
      const [newStaffFromDB] = await Promise.all([
        apiAddStaff(newStaffData, currentUser.id, currentClinicId ?? undefined),
        new Promise(resolve => setTimeout(resolve, 1000))
      ]);
      if (newStaffFromDB) {
        set((state) => ({
          allStaff: [newStaffFromDB, ...state.allStaff],
        }));
        // Audit log (best-effort)
        if (currentClinicId) {
          void insertOperationLog({
            clinicId: currentClinicId,
            authUserId: authUserId ?? undefined,
            actedStaffId: currentUser?.id ?? null,
            action: 'staff.create',
            targetType: 'staff',
            targetId: String(newStaffFromDB.id),
          });
        }
        return newStaffFromDB;
      }
      return null;
    } catch (error) {
      console.error("スタッフの追加中にエラーが発生:", error);
      throw error;
    } finally {
      set({ isAddingStaff: false });
    }
  },
  
  deleteStaff: async (staffId, options) => {
    set({ isDeletingStaff: true });
    try {
      const { currentUser, currentClinicId, authUserId } = get();
      const deleter = apiDeleteStaff;
      const [result] = await Promise.all([
        currentClinicId
          ? deleter({
              staffId,
              clinicId: currentClinicId,
              actedStaffId: currentUser?.id ?? undefined,
              replacementStaffId: options?.replacementStaffId,
              allowUnassigned: options?.allowUnassigned,
            })
          : Promise.resolve({ success: false, error: 'Clinic not selected' }),
        new Promise(resolve => setTimeout(resolve, 1000))
      ]);
      if (result.success) {
        set((state) => ({
          allStaff: state.allStaff.filter(s => s.id !== staffId),
          showStaffFormPanel: false,
          editingStaff: null,
        }));
        // Audit log (best-effort)
        if (currentClinicId) {
          void insertOperationLog({
            clinicId: currentClinicId,
            authUserId: authUserId ?? undefined,
            actedStaffId: currentUser?.id ?? null,
            action: 'staff.delete',
            targetType: 'staff',
            targetId: String(staffId),
          });
        }
      }
      return result;
    } catch (error) {
      console.error("スタッフの削除中にエラーが発生:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'スタッフの削除に失敗しました。',
      };
    } finally {
      set({ isDeletingStaff: false });
    }
  },

  // 【追加】申し送り関連の関数
  loadHandovers: async (patientId?: string) => {
    try {
      const { currentClinicId } = get();
      const handovers = await getAllHandovers(patientId, currentClinicId ?? undefined);
      set({ allHandovers: handovers });
    } catch (error) {
      console.error('申し送りの読み込みに失敗しました:', error);
      set({ allHandovers: [] });
    }
  },

  addHandover: async (handoverData) => {
    set({ isAddingHandover: true });
    try {
      const { currentUser, currentClinicId, authUserId } = get();
      const newHandover = await createHandoverDB({
        ...handoverData,
        createdBy: currentUser?.id || '',
        clinicId: currentClinicId ?? undefined,
      });

      if (newHandover) {
        set((state) => ({
          allHandovers: [newHandover, ...state.allHandovers],
          isAddingHandover: false,
        }));
        // Audit log (best-effort)
        if (currentClinicId) {
          void insertOperationLog({
            clinicId: currentClinicId,
            authUserId: authUserId ?? undefined,
            actedStaffId: currentUser?.id ?? null,
            action: 'handover.create',
            targetType: 'handovers',
            targetId: String(newHandover.id),
            metadata: {
              patientId: String(newHandover.patientId),
            },
          });
        }
        return newHandover;
      }
      set({ isAddingHandover: false });
      return null;
    } catch (error) {
      console.error('申し送りの追加に失敗しました:', error);
      set({ isAddingHandover: false });
      return null;
    }
  },

  updateHandover: async (handoverId, updatedData) => {
    set({ isUpdatingHandover: true });
    try {
      const { currentClinicId, authUserId, currentUser } = get();
      const success = await updateHandoverDB(handoverId, updatedData);
      if (success) {
        set((state) => ({
          allHandovers: state.allHandovers.map(h =>
            h.id === handoverId
              ? { ...h, ...updatedData, updatedAt: new Date().toISOString() }
              : h
          ),
          isUpdatingHandover: false,
        }));
        // Audit log (best-effort)
        if (currentClinicId) {
          void insertOperationLog({
            clinicId: currentClinicId,
            authUserId: authUserId ?? undefined,
            actedStaffId: currentUser?.id ?? null,
            action: 'handover.update',
            targetType: 'handovers',
            targetId: String(handoverId),
          });
        }
        return true;
      }
      set({ isUpdatingHandover: false });
      return false;
    } catch (error) {
      console.error('申し送りの更新に失敗しました:', error);
      set({ isUpdatingHandover: false });
      return false;
    }
  },

  deleteHandover: async (handoverId) => {
    set({ isDeletingHandover: true });
    try {
      const { currentClinicId, authUserId, currentUser } = get();
      const success = await deleteHandoverDB(handoverId);
      if (success) {
        set((state) => ({
          allHandovers: state.allHandovers.filter(h => h.id !== handoverId),
          isDeletingHandover: false,
        }));
        // Audit log (best-effort)
        if (currentClinicId) {
          void insertOperationLog({
            clinicId: currentClinicId,
            authUserId: authUserId ?? undefined,
            actedStaffId: currentUser?.id ?? null,
            action: 'handover.delete',
            targetType: 'handovers',
            targetId: String(handoverId),
          });
        }
        return true;
      }
      set({ isDeletingHandover: false });
      return false;
    } catch (error) {
      console.error('申し送りの削除に失敗しました:', error);
      set({ isDeletingHandover: false });
      return false;
    }
  },

  updateStaff: async (staffId, updatedData) => {
    set({ isUpdatingStaff: true }); // ★ 更新開始
    const { currentUser, logout, currentClinicId, authUserId } = get();

    // updatedByフィールドに現在のユーザーIDを追加
    const dataWithUpdater = {
      ...updatedData,
      updatedBy: currentUser?.id,
    };

    try {
      // 1秒間の待機Promiseを作成
      const delay = new Promise(resolve => setTimeout(resolve, 1000));
      
      // データベース更新と1秒待機を同時に実行し、両方が終わるのを待つ
      const [success] = await Promise.all([
        apiUpdateStaff(staffId, dataWithUpdater),
        delay
      ]);

      if (success) {
        set((state) => {
          const updatedStaff = state.allStaff.find(staff => staff.id === staffId);
          const newStaff = updatedStaff ? { ...updatedStaff, ...updatedData } : null;
          
          return {
            allStaff: state.allStaff.map(staff =>
              staff.id === staffId
                ? {
                    ...staff,
                    ...updatedData,
                    updatedBy: currentUser?.id ?? staff.updatedBy,
                    updatedByName: currentUser?.name ?? staff.updatedByName,
                  }
                : staff
            ),
            // 【重要】editingStaffも更新後のデータで上書きする
            editingStaff:
              state.editingStaff?.id === staffId && newStaff
                ? {
                    ...newStaff,
                    updatedBy: currentUser?.id ?? newStaff.updatedBy,
                    updatedByName: currentUser?.name ?? newStaff.updatedByName,
                  }
                : state.editingStaff,
            staffFormRevision: state.staffFormRevision + 1, // UIアニメーション用
          };
        });

        // Audit log (best-effort)
        if (currentClinicId) {
          void insertOperationLog({
            clinicId: currentClinicId,
            authUserId: authUserId ?? undefined,
            actedStaffId: currentUser?.id ?? null,
            action: 'staff.update',
            targetType: 'staff',
            targetId: String(staffId),
          });
        }

        // ステップ3: 更新対象が自分自身 かつ 管理者権限が解除されたか を判定
        const isSelfDemotion = currentUser?.id === staffId && updatedData.role !== 'admin';
        
        if (isSelfDemotion) {
          // 条件に一致した場合、ログアウト処理を呼び出す
          logout();
        }
        return true; // 成功を返す
      }
      return false; // 失敗を返す
    } catch (error) {
      console.error("スタッフ更新中にエラーが発生:", error);
      return false;
    } finally {
      set({ isUpdatingStaff: false }); // ★ 成功・失敗問わず更新終了
    }
  },
  
  setShowStaffFormPanel: (show) => set({ showStaffFormPanel: show }),
  setEditingStaff: (staff) => {
    set((state) => ({
      editingStaff: staff,
      showStaffFormPanel: true,
      staffFormRevision: state.staffFormRevision + 1, // 常にカウンターを更新
      // PC版ではパネルを閉じず、スマホ版でのみ閉じる（StaffListPanel.tsxで制御）
    }));
  },
  
  // 【追加】ユーザー情報の更新
  setCurrentUser: (user) => set({ currentUser: user }),
  setAuthUserId: (id) => set({ authUserId: id }),
  setIsDeviceUser: (value) => set({ isDeviceUser: value }),
  
  setShowSettingsPanel: (show) => {
    const { showSettingsPanel } = get();
    // ★もし設定パネルが既に開いていたら、カウンターを増やして終了
    if (showSettingsPanel && show) {
      set((state) => ({ settingsPanelRevision: state.settingsPanelRevision + 1 }));
      return;
    }

    // ★それ以外の場合は、元の切り替え動作を実行（設定を開くときは辞書パネルは閉じる）
    if (show) {
      set({ showSettingsPanel: true, showSpeechDictionaryPanel: false });
    } else {
      set({ showSettingsPanel: false });
    }
  },

  setShowSpeechDictionaryPanel: (show) => {
    const { showSpeechDictionaryPanel } = get();
    if (showSpeechDictionaryPanel && show) {
      set((state) => ({ speechDictionaryRevision: state.speechDictionaryRevision + 1 }));
      return;
    }
    set({ showSpeechDictionaryPanel: show });
  },

  openSpeechDictionaryPanel: () =>
    set((s) => ({
      showSpeechDictionaryPanel: true,
      speechDictionaryRevision: s.speechDictionaryRevision + 1,
      isNavigatingBack: false,
      showDatePanel: false,
      showWorkflowStaffListPanel: false,
      showDetailPanel: false,
      detailPanelRevision: s.showDetailPanel ? s.detailPanelRevision + 1 : s.detailPanelRevision,
      showSchedulePanel: false,
      schedulePanelRevision: s.showSchedulePanel ? s.schedulePanelRevision + 1 : s.schedulePanelRevision,
      showSchedulePatientListPanel: false,
      showSettingsPanel: false,
      showStaffListPanel: false,
      showAllPatientListPanel: false,
      showPatientFormPanel: false,
      showStaffFormPanel: false,
      showSettingsDatePanel: false,
      settingsReturningToRoot: false,
      workflowRootRevealFromSettings: false,
      settingsRootRevealFromWorkflow: false,
    })),

  refreshClinicSpeechTerms: async () => {
    const { currentClinicId } = get();
    const terms = await getClinicSpeechTerms(currentClinicId ?? undefined, { refresh: true });
    set({ clinicSpeechTerms: terms });
  },
  setShowAllPatientListPanel: (show) => set({ showAllPatientListPanel: show }),
  setShowStaffListPanel: (show) => {
    const { showStaffListPanel } = get();
    // ★もしスタッフ一覧パネルが既に開いていたら、カウンターを増やして終了
    if (showStaffListPanel && show) {
      set((state) => ({ 
        staffListRevision: state.staffListRevision + 1,
        showStaffFormPanel: false, // ★ スタッフ編集パネルを閉じる処理を追加
        showPatientFormPanel: false, // ★ 患者編集パネルも確実に閉じる
        showAllPatientListPanel: false, // ★ 患者一覧（設定）を確実に閉じる
        showSettingsDatePanel: false,   // ★ 設定側日付も閉じる（保険）
        showSchedulePanel: false,       // ★ スケジュールも閉じる（保険）
      }));
      return;
    }
    
    // ★それ以外の場合は、元の切り替え動作を実行
    // showがfalseの時は、showStaffFormPanelを閉じない（StaffListPanel.tsx側で制御）
    if (show) {
      set({ 
        showStaffListPanel: show,
        showStaffFormPanel: false, // ★ スタッフ一覧を開く時は、フォームパネルを閉じる
        showPatientFormPanel: false,
        showAllPatientListPanel: false,
        showSettingsDatePanel: false,
        showSchedulePanel: false,
        showSpeechDictionaryPanel: false,
      });
    } else {
      // showがfalseの時は、showStaffListPanelだけを閉じる（フォームパネルは開いたまま）
      set({ showStaffListPanel: show });
    }
  },
  setShowWorkflowStaffListPanel: (show) => {
    const { showWorkflowStaffListPanel } = get();
    // ★もし診療フロー用スタッフ一覧パネルが既に開いていたら、カウンターを増やして終了
    if (showWorkflowStaffListPanel && show) {
      set((state) => ({ 
        workflowStaffListRevision: state.workflowStaffListRevision + 1,
        // 設定ワークフローのパネルを閉じる
        showSettingsPanel: false,
        showSpeechDictionaryPanel: false,
        showStaffListPanel: false,
        showAllPatientListPanel: false,
        showPatientFormPanel: false,
        showStaffFormPanel: false,
        showSettingsDatePanel: false,
      }));
      return;
    }
    
    // ★それ以外の場合は、元の切り替え動作を実行
    set({ 
      showWorkflowStaffListPanel: show,
      // 設定ワークフローのパネルを閉じる
      showSettingsPanel: false,
      showSpeechDictionaryPanel: false,
      showStaffListPanel: false,
      showAllPatientListPanel: false,
      showPatientFormPanel: false,
      showStaffFormPanel: false,
      showSettingsDatePanel: false,
    });
  },
  setShowSettingsDatePanel: (show) => {
    const { showSettingsDatePanel } = get();
    if (showSettingsDatePanel && show) {
      set((state) => ({ settingsDatePanelRevision: state.settingsDatePanelRevision + 1 }));
      return;
    }
    set({ showSettingsDatePanel: show });
  },
  setShowDatePanel: (show) => set({ showDatePanel: show }),
  setShowDetailPanel: (show) => {
    const { showDetailPanel } = get();
    // ★もし詳細パネルが既に開いていたら、カウンターを増やして終了
    if (showDetailPanel && show) {
      set((state) => ({ detailPanelRevision: state.detailPanelRevision + 1 }));
      return;
    }
    
    // ★閉じる場合もリビジョンを更新して残像を防ぐ
    if (showDetailPanel && !show) {
      set((state) => ({ 
        showDetailPanel: false,
        detailPanelRevision: state.detailPanelRevision + 1 
      }));
      return;
    }
    
    // ★それ以外の場合は、元の切り替え動作を実行
    set({ showDetailPanel: show });
  },

  setShowSchedulePanel: (show) => {
    const { showSchedulePanel, scheduleSelectedStaffId } = get();
    // ★もしスケジュールパネルが既に開いていたら、カウンターを増やして終了
    if (showSchedulePanel && show) {
      set((state) => ({ schedulePanelRevision: state.schedulePanelRevision + 1 }));
      return;
    }
    
    // ★閉じる場合もリビジョンを更新して残像を防ぐ（申し送りパネルと同様）
    if (showSchedulePanel && !show) {
      set((state) => ({ 
        showSchedulePanel: false,
        schedulePanelRevision: state.schedulePanelRevision + 1 
      }));
      return;
    }
    
    // ★開く場合でも、scheduleSelectedStaffIdがnullの場合は開かない
    if (show && !scheduleSelectedStaffId) {
      return;
    }
    
    // ★それ以外の場合は、元の切り替え動作を実行
    set({ showSchedulePanel: show });
  },

  setShowSchedulePatientListPanel: (show) => {
    const { showSchedulePatientListPanel } = get();
    if (showSchedulePatientListPanel && show) {
      set((state) => ({ schedulePatientListRevision: state.schedulePatientListRevision + 1 }));
      return;
    }
    set({ showSchedulePatientListPanel: show });
  },

  setScheduleSelectedTime: (time) => set({ scheduleSelectedTime: time }),
  setScheduleSelectedStaffId: (id) => {
    const { showSchedulePanel } = get();
    // パネルが開いている場合は、まず閉じてからスタッフIDを更新（残像防止）
    if (showSchedulePanel) {
      set((state) => ({
        scheduleSelectedStaffId: id,
        scheduleAppointmentsByTime: {},
        showSchedulePanel: false,
        schedulePanelRevision: state.schedulePanelRevision + 1,
      }));
    } else {
      set({ scheduleSelectedStaffId: id, scheduleAppointmentsByTime: {} });
    }
  },

  reserveSelectedSlotWithPatient: async (patientId) => {
    const { scheduleSelectedTime, scheduleSelectedStaffId, selectedDate, allPatients, currentClinicId, authUserId, currentUser } = get();
    if (!scheduleSelectedTime || !scheduleSelectedStaffId || !selectedDate) {
      console.error('予約に必要な情報が不足しています');
      return false;
    }

    try {
      // 既存予約の簡易重複チェック（B案: 直接DB）
      // 注意: フロント側チェックはUX向上のため維持。サーバ側でも最終チェックあり
      const ymd = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth()+1).padStart(2,'0')}-${String(selectedDate.getDate()).padStart(2,'0')}`;
      try {
        const api = await import('../lib/api');
        const list = await api.getAppointmentsDirect(ymd, scheduleSelectedStaffId, currentClinicId ?? undefined);
        const exists = Array.isArray(list) && list.some((a) => a.appointment_time === scheduleSelectedTime);
        if (exists) {
          console.warn('同一枠に既に予約があります');
          return false;
        }
      } catch (err) {
        // エラーを無視せず、最低限ログ出力
        console.warn('予約重複チェック中にエラーが発生しました:', err);
      }

      const api = await import('../lib/api');
      const created = await api.createAppointmentSafe({
        doctorId: scheduleSelectedStaffId,
        patientId,
        appointmentDate: ymd,
        appointmentTime: scheduleSelectedTime,
        status: 'scheduled',
        clinicId: currentClinicId ?? undefined,
      });
      // Audit log (best-effort)
      if (currentClinicId) {
        void insertOperationLog({
          clinicId: currentClinicId,
          authUserId: authUserId ?? undefined,
          actedStaffId: currentUser?.id ?? null,
          action: 'appointment.create',
          targetType: 'appointments',
          targetId: String(created.id),
          metadata: {
            doctorId: String(scheduleSelectedStaffId),
            patientId: String(patientId),
            appointmentDate: ymd,
            appointmentTime: scheduleSelectedTime,
          },
        });
      }
      // 成功時も予約登録パネルは閉じない（続けて登録できるようにする）
      const patient = allPatients.find(p => String(p.id) === String(patientId));
      set((state) => ({
        scheduleAppointmentsByTime: {
          ...state.scheduleAppointmentsByTime,
          [scheduleSelectedTime!]: { patientId, patientName: patient?.name || '' }
        }
      }));
      return true;
    } catch (e) {
      // AppointmentErrorの処理
      if (e instanceof AppointmentError) {
        // 予約登録パネルは閉じない（再試行可能にする）
        console.error('予約作成エラー:', e.message);
        return false;
      }
      
      // その他のエラー（予期しないエラー）
      console.error('予約作成に失敗しました', e);
      return false;
    }
  },

  cancelAppointmentAtSelectedSlot: async () => {
    const { scheduleSelectedTime, scheduleSelectedStaffId, selectedDate, currentClinicId, authUserId, currentUser } = get();
    if (!scheduleSelectedTime || !scheduleSelectedStaffId || !selectedDate) {
      console.error('キャンセルに必要な情報が不足しています');
      return false;
    }
    try {
      const api = await import('../lib/api');
      const ymd = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth()+1).padStart(2,'0')}-${String(selectedDate.getDate()).padStart(2,'0')}`;
      const ok = await api.deleteAppointmentByKey(ymd, scheduleSelectedTime, scheduleSelectedStaffId, currentClinicId ?? undefined);
      if (ok) {
        set((state) => {
          const map = { ...state.scheduleAppointmentsByTime };
          delete map[scheduleSelectedTime!];
          return { scheduleAppointmentsByTime: map };
        });
        // Audit log (best-effort) - key-based delete doesn't return id, so log composite key
        if (currentClinicId) {
          void insertOperationLog({
            clinicId: currentClinicId,
            authUserId: authUserId ?? undefined,
            actedStaffId: currentUser?.id ?? null,
            action: 'appointment.cancel',
            targetType: 'appointments',
            targetId: `${ymd} ${scheduleSelectedTime} doctor:${scheduleSelectedStaffId}`,
            metadata: {
              doctorId: String(scheduleSelectedStaffId),
              appointmentDate: ymd,
              appointmentTime: scheduleSelectedTime,
            },
          });
        }
      }
      return ok;
    } catch (e) {
      console.error('予約キャンセルに失敗しました', e);
      return false;
    }
  },

  loadScheduleAppointments: async () => {
    const { scheduleSelectedStaffId, selectedDate, allPatients, currentClinicId } = get();
    if (!scheduleSelectedStaffId || !selectedDate) {
      set({ scheduleAppointmentsByTime: {} });
      return;
    }
    // 日付・スタッフ切替直後、前の結果が残ると別日の予約が同じ時刻枠に緑表示されるため一旦クリア
    set({ scheduleAppointmentsByTime: {} });
    const ymd = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth()+1).padStart(2,'0')}-${String(selectedDate.getDate()).padStart(2,'0')}`;
    try {
      const api = await import('../lib/api');
      const list = await api.getAppointmentsDirect(ymd, scheduleSelectedStaffId, currentClinicId ?? undefined);
      const map: Record<string, { patientId: string; patientName: string }> = {};
      for (const a of list as any[]) {
        const pid = a.patient_id || a.patientId;
        const time = a.appointment_time || a.appointmentTime;
        const patient = allPatients.find(p => String(p.id) === String(pid));
        if (time) map[time] = { patientId: String(pid), patientName: patient?.name || '' };
      }
      set({ scheduleAppointmentsByTime: map });
    } catch (e) {
      console.error('スケジュール読み込みに失敗しました', e);
      set({ scheduleAppointmentsByTime: {} });
    }
  },

  subscribeSchedule: () => {
    const { currentClinicId } = get();
    const apiImport = import('../lib/api');
    apiImport.then(({ subscribeAppointments }) => {
      const unsubscribe = subscribeAppointments(async () => {
        await get().loadScheduleAppointments();
      }, currentClinicId ?? undefined);
      // 保存: ウィンドウ閉じ時に解除
      window.addEventListener('beforeunload', () => {
        try { (unsubscribe as any)?.(); } catch {}
      });
    }).catch(() => {});
  },

  // 【重要】設定から「患者一覧」を開くための専用関数
  openAllPatientList: () => {
    const { showAllPatientListPanel } = get();
    // ★もし患者一覧パネルが既に開いていたら、カウンターを増やして終了
    if (showAllPatientListPanel) {
      set((state) => ({ 
        patientListRevision: state.patientListRevision + 1,
        showPatientFormPanel: false, // ★ 患者編集パネルを閉じる処理を追加
        showSettingsDatePanel: false,
        showSchedulePanel: false,
        showSchedulePatientListPanel: false, // ★ 予約登録パネルも必ず閉じる
      }));
      return;
    }
    
    // ★それ以外の場合は、元の切り替え動作を実行
    set({
      showAllPatientListPanel: true,
      showPatientFormPanel: false, // ★ こちらにも追加
      showSettingsDatePanel: false,
      showSchedulePanel: false,
      showSchedulePatientListPanel: false, // ★ 予約登録パネルも必ず閉じる
      // 他のワークフローは全て閉じる
      showDatePanel: false,
      showDetailPanel: false,
      // 他の設定パネルも閉じる
      showStaffListPanel: false,
      // スタッフ関連の状態をリセット
      showStaffFormPanel: false,
      editingStaff: null,
    });
  },

  // 【重要】日付が選択された際の処理を修正
  setSelectedDate: (date) => {
    const { showDatePanel, showSettingsDatePanel } = get();
    
    // 設定レーンの日時選択時は、設定用患者一覧を開く（他は触らない）
    if (showSettingsDatePanel) {
      set((state) => ({
        selectedDate: date,
        hasUserSelectedDate: true,
        // PC版ではパネルを閉じず、スマホ版でのみ閉じる（MonthCalendarPanel.tsxで制御）
        showAllPatientListPanel: false, // まず患者一覧は閉じる（新方針）
        patientListContext: 'settings',
        schedulePanelSource: 'settings', // 設定フローから開いたことを記録（予約登録可の判定に使用）
        showSchedulePanel: true, // スケジュールを表示
        schedulePanelRevision: state.schedulePanelRevision + 1, // 日付再選択で開き直し
        showSchedulePatientListPanel: false, // 予約登録パネルは必ず閉じる
        // 設定フローでは申し送りは閲覧のみ。日付変更時は必ず閉じる。
        showDetailPanel: false,
        selectedPatientId: null,
        detailPanelRevision: state.detailPanelRevision + 1,
      }));
      return;
    }

    // 診療フロー用：日付が既に選択されていて、月カレンダーが開いている場合は、日付だけ更新して開き直さない
    if (showDatePanel && !showSettingsDatePanel) {
      set((state) => ({
        selectedDate: date,
        hasUserSelectedDate: true,
        // 月カレンダーは開き直さない（datePanelRevisionは増やさない）
      }));
      return;
    }

    // ★それ以外の場合は、元の切り替え動作を実行
    set({
      selectedDate: date,
      hasUserSelectedDate: true,
      showDatePanel: true,
      // 【修正】スタッフ選択時にドクターパネルが勝手に開かないように修正
      showDetailPanel: false,    // 詳細パネルも非表示に
      selectedPatientId: null,   // 選択された患者をリセット
    });
  },

  // 【重要】患者が選択された際の処理（申し送りパネル用）
  setSelectedPatient: (id) => {
    const { showDetailPanel, selectedPatientId } = get();
    // ★もし同じ患者が既に選択されていたら、カウンターを増やして終了
    if (showDetailPanel && selectedPatientId === id) {
      set((state) => ({ detailPanelRevision: state.detailPanelRevision + 1 }));
      return;
    }
    
    // ★それ以外の場合は、元の切り替え動作を実行
    set({ 
      selectedPatientId: id,
      showDetailPanel: true,     // 詳細パネルは表示
    });
  },
  
  // 【追加】設定から患者一覧を開くための新しい関数
  openPatientListFromSettings: () => {
    const { showAllPatientListPanel } = get();
    // ★もし設定用患者一覧パネルが既に開いていたら、カウンターを増やして終了
    if (showAllPatientListPanel) {
      set((state) => ({ patientListRevision: state.patientListRevision + 1 }));
      return;
    }
    
    // ★それ以外の場合は、元の切り替え動作を実行
    set({
      showSettingsPanel: false,
      showAllPatientListPanel: true,
      patientListContext: 'settings', // 設定として開くことを明記
      showSpeechDictionaryPanel: false,
      // 他のワークフローは閉じる
      showDatePanel: false,
      showDetailPanel: false,
    });
  },
  
  resetPanels: () => set({
    selectedPatientId: null,
    showDetailPanel: false
  }),

  // 【重要】カレンダーアイコン用のトグル機能を修正
  togglePanels: () => {
    const { showDatePanel, showDetailPanel } = get();
    const isDateWorkflowOpen = showDatePanel || showDetailPanel;

    if (isDateWorkflowOpen) {
      // 日付関連のパネルが開いていたら、全て閉じる
      set({
        showDatePanel: false,
        showDetailPanel: false,
        showSpeechDictionaryPanel: false,
        // 設定系の編集パネルも確実に閉じる
        showPatientFormPanel: false,
        showStaffFormPanel: false,
        editingPatient: null,
        editingStaff: null,
      });
    } else {
      // 全て閉じていたら、初期状態に戻す（日付パネルを開く）
      set({
        showDatePanel: true,
        selectedDate: new Date(),
        selectedPatientId: null,
        showDetailPanel: false,
        // 【追加】設定関連のパネルは確実に閉じる
        showSettingsPanel: false,
        showSpeechDictionaryPanel: false,
        showAllPatientListPanel: false,
        showStaffListPanel: false,
        // 設定系の編集パネルも確実に閉じる
        showPatientFormPanel: false,
        showStaffFormPanel: false,
        editingPatient: null,
        editingStaff: null,
      });
    }
  },

  // 【重要】設定アイコン用のオン/オフスイッチ機能を修正
  toggleSettingsWorkflow: () => {
    const { showSettingsPanel, showAllPatientListPanel, showStaffListPanel, showPatientFormPanel } = get();
    // 設定関連のパネルが開いているかチェック (患者フォームパネルもチェック対象に加える)
    const isSettingsWorkflowOpen = showSettingsPanel || showAllPatientListPanel || showStaffListPanel || showPatientFormPanel;

    if (isSettingsWorkflowOpen) {
      // 設定関連のパネルが開いていたら、全て閉じる
      set({
        showSettingsPanel: false,
        showSpeechDictionaryPanel: false,
        showAllPatientListPanel: false,
        showStaffListPanel: false,
        showPatientFormPanel: false, // 【修正】患者フォームパネルも閉じる
        showStaffFormPanel: false,   // スタッフフォームパネルも閉じる
        editingPatient: null,
        editingStaff: null,
      });
    } else {
      // 全て閉じていたら、設定パネルを開く
      set({
        showSettingsPanel: true,
        showSpeechDictionaryPanel: false,
        // 念のため他のワークフローは閉じる
        showDatePanel: false,
        showDetailPanel: false,
        // 設定系の編集パネルも確実に閉じる
        showPatientFormPanel: false,
        showStaffFormPanel: false,
        editingPatient: null,
        editingStaff: null,
      });
    }
  },

  // resetは残しておいても良いですが、togglePanelsが主役になります
  reset: () => set({
    showDatePanel: true,
    showSettingsPanel: false,
    showSpeechDictionaryPanel: false,
    showAllPatientListPanel: false,
    showStaffListPanel: false,
    selectedDate: new Date(),
    selectedPatientId: null,
    showDetailPanel: false,
    patientListContext: 'workflow',
    settingsReturningToRoot: false,
    workflowRootRevealFromSettings: false,
    settingsRootRevealFromWorkflow: false,
  }),

  // ログアウト処理
  logout: () => {
    clearClinicSpeechTermsSessionCache();
    // セッション情報をクリア
    sessionStorage.removeItem('sessionToken');
    sessionStorage.removeItem('staff');
    // ストアの状態をリセット
    set({
      currentUser: null,
      showDatePanel: false,
      showSettingsPanel: false,
      showSpeechDictionaryPanel: false,
      showStaffFormPanel: false,
      showAllPatientListPanel: false,
      showStaffListPanel: false,
      showPatientFormPanel: false,
      showDetailPanel: false,
      selectedDate: null,
      selectedPatientId: null,
      patientListContext: 'workflow',
      clinicSpeechTerms: [],
      settingsReturningToRoot: false,
      workflowRootRevealFromSettings: false,
      settingsRootRevealFromWorkflow: false,
    });
  },
}));
